import { useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Delete,
  Edit,
} from '@mui/icons-material';
import { t } from 'i18next';
import { colors } from '../../styles';
import logo from '../../flag-icon.png';
import { buildPlateOptions, plateEquals, PlateLike } from '../utils/plates';

interface PlateSelectorProps {
  vrm: string;
  plates: PlateLike[];
  typedVrm: string;
  onPlateChange: (value: string) => void;
  markPlateToDelete: (
    id: number,
  ) => (event: React.MouseEvent<HTMLButtonElement>) => void;
}

// `t` is typed as possibly returning null, but aria-label needs a plain string.
const label = (key: string, fallback: string): string =>
  t(key, fallback) ?? fallback;

const navButtonSx = {
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: colors.white,
  width: 32,
  height: 32,
  flexShrink: 0,
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
};

const plateIconSx = { flexShrink: 0, padding: '4px' };

// The flag block and the action area are the same fixed width, so the plate
// number sits at the true centre of the plate and never shifts when a button
// appears or disappears. The buttons pack to the right inside that fixed area.
const PLATE_EDGE_WIDTH = 68;

export const PlateSelector: React.FC<PlateSelectorProps> = ({
  vrm,
  plates,
  typedVrm,
  onPlateChange,
  markPlateToDelete,
}) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>('');
  // Escape unmounts the input, which can also fire blur; this stops the blur
  // handler from committing a cancelled edit.
  const skipCommitRef = useRef<boolean>(false);

  const options = useMemo(
    () => buildPlateOptions(typedVrm, plates),
    [typedVrm, plates],
  );
  const activeIndex = options.findIndex((option) =>
    plateEquals(option.vrm, vrm),
  );
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;
  const showArrows = options.length > 1;

  const stepBy = (delta: number) => {
    if (options.length === 0) return;
    const from = activeIndex >= 0 ? activeIndex : 0;
    const next = (from + delta + options.length) % options.length;
    onPlateChange(options[next].vrm);
  };

  const startEdit = () => {
    setDraft(vrm);
    setEditing(true);
  };

  const commitEdit = () => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      setEditing(false);
      return;
    }
    const next = draft.trim();
    setEditing(false);
    // Never commit empty: an empty vrm collapses the whole payment UI.
    if (!next || plateEquals(next, vrm)) return;
    onPlateChange(next);
  };

  const cancelEdit = () => {
    skipCommitRef.current = true;
    setEditing(false);
  };

  return (
    <Stack width="100%" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
      <Stack
        direction="row"
        width="100%"
        alignItems="center"
        justifyContent="center"
        spacing={1}
      >
        {showArrows && (
          <IconButton
            sx={navButtonSx}
            onClick={() => stepBy(-1)}
            aria-label={label('previousPlate', 'Previous registration')}
          >
            <ChevronLeft />
          </IconButton>
        )}
        <Box
          sx={{
            border: '1px solid black',
            flex: 1,
            minWidth: 0,
            maxWidth: 340,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: colors.white,
          }}
        >
          <Box
            sx={{
              width: PLATE_EDGE_WIDTH,
              flexShrink: 0,
              backgroundColor: colors.plateBlue,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src={logo} alt="Flag logo" width="55%" />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.5,
            }}
          >
            {editing ? (
              <TextField
                autoFocus
                variant="standard"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitEdit();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEdit();
                  }
                }}
                inputProps={{
                  'aria-label': label(
                    'vehicleRegistration',
                    'Vehicle registration',
                  ),
                  style: {
                    textAlign: 'center',
                    fontSize: 24,
                    padding: 0,
                    textTransform: 'uppercase',
                    color: colors.black,
                  },
                }}
                sx={{ width: '100%' }}
              />
            ) : (
              <Box
                sx={{
                  fontSize: { xs: 24, sm: 30 },
                  color: colors.black,
                  letterSpacing: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {vrm.toUpperCase()}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: PLATE_EDGE_WIDTH,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {editing ? (
              <IconButton
                sx={plateIconSx}
                onMouseDown={(e) => e.preventDefault()}
                onClick={commitEdit}
                aria-label={label('confirm', 'Confirm')}
              >
                <Check fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                sx={plateIconSx}
                onClick={startEdit}
                aria-label={label('editPlate', 'Edit registration')}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
            {!editing && activeOption?.kind === 'saved' && (
              <IconButton
                sx={plateIconSx}
                onClick={markPlateToDelete(activeOption.id)}
                aria-label={label('delete', 'Delete')}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        {showArrows && (
          <IconButton
            sx={navButtonSx}
            onClick={() => stepBy(1)}
            aria-label={label('nextPlate', 'Next registration')}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Stack>
      {showArrows && (
        <Stack direction="row" spacing={0.75} justifyContent="center">
          {options.map((option, index) => (
            <Box
              key={`${option.kind}-${option.vrm}`}
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor:
                  index === activeIndex ? colors.orange : '#d2d2d2',
              }}
            />
          ))}
        </Stack>
      )}
      {activeOption && (
        <Typography sx={{ fontSize: 12, color: '#6b6b6b' }}>
          {activeOption.kind === 'typed'
            ? label('typedPlate', 'Typed plate')
            : label('savedPlate', 'Saved plate')}
          {showArrows && ` · ${activeIndex + 1} of ${options.length}`}
        </Typography>
      )}
    </Stack>
  );
};
